import { useRef, useState } from "react";
import { Search, Loader2, MapPin, X } from "lucide-react";
import { useNominatim, formatAddress, type NominatimCandidate } from "../../hooks/useNominatim";
import clsx from "clsx";

interface Props {
  label: string;
  onPlzSelect: (plz: string) => void;
  error?: string;
  hint?: string;
  disabled?: boolean;
}

/**
 * Adress-Autocomplete-Feld mit Dropdown, Tastaturnavigation und ARIA.
 * Extrahiert bei Auswahl die PLZ aus dem Geocoding-Ergebnis.
 */
export default function AddressAutocomplete({ label, onPlzSelect, error, hint, disabled }: Props) {
  const id = `address-autocomplete-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
  const { query, search, forceSearch, candidates, loading, clear } = useNominatim({ disabled });
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [confirmed, setConfirmed] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = `${id}-list`;

  const handleSelect = (c: NominatimCandidate) => {
    const plz = c.address.postcode ?? "";
    const display = formatAddress(c);
    setOpen(false);
    setActiveIndex(-1);
    setConfirmed(display);
    clear();
    if (inputRef.current) inputRef.current.value = display;
    if (plz) onPlzSelect(plz);
  };

  const handleReset = () => {
    setConfirmed(null);
    if (inputRef.current) {
      inputRef.current.value = "";
      inputRef.current.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open || candidates.length === 0) {
      if (e.key === "Enter") {
        e.preventDefault();
        const value = inputRef.current?.value ?? "";
        if (value.trim().length >= 3) { forceSearch(value); setOpen(true); }
      }
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, candidates.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? -1 : i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIndex >= 0) handleSelect(candidates[activeIndex]);
      else handleSelect(candidates[0]);
    } else if (e.key === "Escape") {
      setOpen(false);
      setActiveIndex(-1);
    }
  };

  return (
    <div className="relative mb-4">
      <label htmlFor={id} className={clsx("block text-sm font-medium mb-1", error ? "text-neko-red" : "text-neko-gray-light")}>
        {label}
      </label>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neko-gray pointer-events-none" />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neko-gray animate-spin" />
        )}
        {confirmed && !loading && (
          <button
            type="button"
            onMouseDown={handleReset}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neko-gray hover:text-neko-orange transition-colors"
            aria-label="Adresse zurücksetzen"
          >
            <X className="w-4 h-4" />
          </button>
        )}
        <input
          ref={inputRef}
          id={id}
          type="text"
          placeholder="z.B. Hauptstraße 1, 78652 Deißlingen"
          defaultValue={query}
          disabled={disabled}
          role="combobox"
          aria-expanded={open && candidates.length > 0}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={activeIndex >= 0 ? `${listId}-option-${activeIndex}` : undefined}
          aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
          onChange={(e) => {
            setConfirmed(null);
            search(e.target.value);
            setOpen(true);
            setActiveIndex(-1);
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => { if (!confirmed) setOpen(true); }}
          onBlur={() => setTimeout(() => { setOpen(false); setActiveIndex(-1); }, 150)}
          className={clsx(
            "w-full pl-9 pr-9 py-3 bg-neko-dark-2 border rounded-xl text-neko-white placeholder-neko-gray text-sm",
            "focus:outline-none focus:border-neko-orange transition-colors",
            error ? "border-neko-red" : "border-neko-dark-3",
            disabled && "opacity-50 cursor-not-allowed"
          )}
          autoComplete="off"
          data-testid="address-search-input"
        />
      </div>

      {open && candidates.length > 0 && (
        <ul
          id={listId}
          role="listbox"
          aria-label={label}
          className="absolute z-50 mt-1 w-full bg-neko-dark-2 border border-neko-dark-3 rounded-xl overflow-hidden shadow-xl max-h-60 overflow-y-auto"
          data-testid="address-dropdown"
        >
          {candidates.map((c, i) => (
            <li
              key={c.place_id}
              id={`${listId}-option-${i}`}
              role="option"
              aria-selected={i === activeIndex}
            >
              <div
                onMouseDown={(e) => { e.preventDefault(); handleSelect(c); }}
                className={clsx(
                  "w-full flex items-start gap-3 px-4 py-3 text-sm transition-colors cursor-pointer",
                  "text-neko-white",
                  i === activeIndex ? "bg-neko-dark-3" : "hover:bg-neko-dark-3/60"
                )}
              >
                <MapPin className="w-4 h-4 text-neko-orange flex-shrink-0 mt-0.5" />
                <span className="line-clamp-2">{formatAddress(c)}</span>
              </div>
            </li>
          ))}
        </ul>
      )}

      {hint && !error && (
        <p id={`${id}-hint`} className="text-xs text-neko-gray mt-1">{hint}</p>
      )}
      {error && (
        <p id={`${id}-error`} className="text-xs text-neko-red mt-1" role="alert">{error}</p>
      )}
    </div>
  );
}
